import { CreateObjectURL } from "@pdfking/compatibility"
import type { PdfEngine, SinglePagePdf, VirtualFile } from "@pdfking/pdfengine"
import { FileContentGUID } from "@pdfking/pdfengine"
import { Init } from "@pdfking/reactivity/preact"
import { CacheFunction } from "@pdfking/storages"
import type { FileName } from "@pdfking/structs"
import { Items, KeyDrop, Only, RecordMerge } from "@pdfking/tfunc"

import type { FileStore } from "#public"

import { CreateFileStore } from "./CreateFileStore"

/**
 * The reactive file collection: filename → per-file {@link FileStore}. Only owns
 * membership — `add`/`remove` — and leaves the split to the per-file store the
 * caller drives (`entry.process()`). Pages are rasterized on demand, through
 * `rasterize`.
 */
export const CreateFileCollectionStore = (
  engine: PdfEngine,
  initial: Record<FileName, VirtualFile> = {}
) => {
  // Any page's raster url, once per page content: an upload's split page and a
  // generated one alike (the engine's stream read to its end, not left open
  // after the first image: an open stream keeps its task waiting on a reader)
  const rasterize = CacheFunction(
    (page: SinglePagePdf): Promise<string> =>
      Only(engine.RasterizePdf([page]))
        .then(image => engine.RequestFile(image))
        .then(response => response.blob())
        .then(CreateObjectURL),
    FileContentGUID
  )

  const store = Init({
    data: {
      files: {} as Record<FileName, FileStore>,
    },
    methods: {
      // Idempotent per name: an existing entry is kept (same-name replacement is
      // a `remove` + `add`). Split/rasterize is the caller's to drive off the
      // returned store.
      add(filename: FileName, file: VirtualFile): FileStore {
        const existing = this.files[filename]
        if (existing) return existing
        const entry = CreateFileStore(engine, file, rasterize)
        this.files = RecordMerge(this.files, { [filename]: entry })
        return entry
      },
      remove(filename: FileName) {
        this.files = KeyDrop(this.files, [filename])
      },
      // The raster url of a page every viewer shows: the flat plot's media
      // and the 3D book's sheets both ask through it
      rasterize(page: SinglePagePdf): Promise<string> {
        return rasterize(page)
      },
    },
  })

  for (const [name, file] of Items(initial)) {
    store.add(name as FileName, file)
  }

  return store
}

export type FileCollectionStore = ReturnType<typeof CreateFileCollectionStore>
